import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@homeleite/arch';
import { timeout } from 'rxjs/operators';

interface LotItem {
    id: string;
    tenantId: string | null;
    producerId: string | null;
    start: string | null;
    end: string | null;
    kind: 'fetch' | 'submit' | 'other';
    scheduled: boolean;
    failed: boolean;
    mirrorVerified?: boolean | null;
    mirrorDetail?: string | null;
}

interface LogItem {
    id: string;
    start: string | null;
    end: string | null;
    entity: string | null;
    entityId: string | null;
    producerId: string | null;
    ok: boolean;
    error: string | null;
    attempt?: number | null;
    correlationId?: string | null;
}

const KIND_LABELS: Record<string, string> = {
    fetch: 'Busca',
    submit: 'Envio',
    other: 'Outro'
};

@Component({
    selector: 'app-lots-panel',
    templateUrl: './lots-panel.component.html',
    styleUrls: ['./lots-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LotsPanelComponent implements OnInit {
    @Input() tenants: string[] = [];

    state: 'loading' | 'error' | 'ready' = 'loading';
    items: LotItem[] = [];
    tenantId = '';
    kind = '';
    onlyFailed = false;
    days = '1';
    limit = 50;
    openId: string | null = null;
    logs: LogItem[] = [];
    logsState: 'idle' | 'loading' | 'error' = 'idle';
    onlyFailedLogs = false;

    constructor(private api: ApiService, private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.load();
    }

    kindLabel(l: LotItem): string {
        return (KIND_LABELS[l.kind] || l.kind) + (l.scheduled ? ' agendada' : '');
    }

    duration(l: LotItem): string {
        if (!l.start || !l.end) return l.end ? '' : 'em andamento';
        const ms = new Date(l.end).getTime() - new Date(l.start).getTime();
        return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
    }

    load(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        let params = new HttpParams().set('limit', this.limit);
        if (this.tenantId) params = params.set('tenantId', this.tenantId);
        if (this.kind) params = params.set('kind', this.kind);
        if (this.onlyFailed) params = params.set('failed', 'true');
        if (this.days) params = params.set('from', new Date(Date.now() - Number(this.days) * 86400000).toISOString());
        this.api.root
            .path('integration-sync', 'lots')
            .getJson<LotItem[]>({ params })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.items = r;
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.state = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    toggle(l: LotItem): void {
        if (this.openId === l.id) {
            this.openId = null;
            this.cd.markForCheck();
            return;
        }
        this.openId = l.id;
        this.onlyFailedLogs = l.failed;
        this.loadLogs();
    }

    loadLogs(): void {
        if (!this.openId) return;
        const id = this.openId;
        this.logs = [];
        this.logsState = 'loading';
        this.cd.markForCheck();
        let params = new HttpParams().set('lotId', id).set('limit', 200);
        if (this.onlyFailedLogs) params = params.set('failed', 'true');
        this.api.root
            .path('integration-sync', 'lots', 'logs')
            .getJson<LogItem[]>({ params })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    if (this.openId !== id) return;
                    this.logs = r;
                    this.logsState = 'idle';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.logsState = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    toggleOnlyFailedLogs(): void {
        this.onlyFailedLogs = !this.onlyFailedLogs;
        this.loadLogs();
    }
}
